import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, Truck, Minus, Plus, Trash, ArrowRight, SignIn } from '@phosphor-icons/react';
import { useAuth } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';

const FloatingCart = ({
  cartItems, removeItem, updateQuantity, cartTotal, addItem,
  colors, getLink, storeSlug, deliveryInfo, products = [],
  floatingCartOpen, setFloatingCartOpen
}) => {
  const { user } = useAuth();
  const { taxBreakdown } = useCart();
  const primary = colors?.primary || '#111111';
  const accent = colors?.accent || '#FF004D';
  const FREE_DELIVERY_THRESHOLD = deliveryInfo?.free_threshold || 50;
  const deliveryProgress = Math.min(100, ((cartTotal || 0) / FREE_DELIVERY_THRESHOLD) * 100);
  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - (cartTotal || 0));
  const cartProductIds = (cartItems || []).map(i => i.product_id);

  const suggested = (products || [])
    .filter(p => !cartProductIds.includes(p.id) && p.stock_status !== 'outofstock' && parseFloat(p.price) > 0)
    .slice(0, 4);

  const count = cartItems?.length || 0;
  const close = () => setFloatingCartOpen(false);

  if (!floatingCartOpen) return null;

  return (
    <div className="fixed inset-0 z-[120]" data-testid="floating-cart" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="absolute inset-0 bg-black/50 animate-[cartFade_0.2s_ease]" onClick={close} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-[420px] bg-white flex flex-col shadow-2xl"
        style={{ animation: 'cartSlideIn 0.28s cubic-bezier(0.22,1,0.36,1)' }}>
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <ShoppingCart size={22} weight="fill" style={{ color: accent }} />
            <span className="text-[18px] font-semibold text-slate-900">Mon panier</span>
            {count > 0 && <span className="text-[12px] text-slate-400 font-medium">({count})</span>}
          </div>
          <button onClick={close} className="text-slate-400 hover:text-slate-700 p-1.5" aria-label="Fermer" data-testid="floating-cart-close"><X size={22} weight="bold" /></button>
        </div>

        {/* Free delivery progress */}
        {count > 0 && (
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 shrink-0" data-testid="delivery-progress">
            <div className="flex items-center gap-2 mb-1.5">
              <Truck size={15} weight="fill" className={deliveryProgress >= 100 ? 'text-emerald-600' : ''} style={deliveryProgress < 100 ? { color: accent } : {}} />
              {deliveryProgress >= 100
                ? <span className="text-[12.5px] font-semibold text-emerald-700">Livraison gratuite débloquée !</span>
                : <span className="text-[12.5px] text-slate-600">Plus que <strong style={{ color: accent }}>{remaining.toFixed(2)} €</strong> pour la livraison offerte</span>}
            </div>
            <div className="w-full h-[6px] bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${deliveryProgress}%`, backgroundColor: deliveryProgress >= 100 ? '#10b981' : accent }} />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {count === 0 ? (
            <div className="text-center py-16 px-6">
              <ShoppingCart size={44} className="mx-auto mb-3 text-slate-200" />
              <p className="text-[15px] font-semibold text-slate-700">Votre panier est vide</p>
              <p className="text-[13px] text-slate-400 mt-1">Découvrez nos lunettes et ajoutez vos favoris.</p>
              <button onClick={close} className="mt-5 px-6 py-3 rounded-full text-white text-[14px] font-semibold" style={{ backgroundColor: primary }}>Continuer mes achats</button>
            </div>
          ) : (
            <div className="px-4 py-3 divide-y divide-slate-50">
              {cartItems.map((item, idx) => (
                <div key={`${item.product_id}-${idx}`} className="flex items-center gap-3 py-3" data-testid={`floating-cart-item-${item.product_id}`}>
                  {item.image
                    ? <img src={item.image} alt={item.name} className="w-[58px] h-[58px] rounded-xl object-contain bg-slate-50 p-1 flex-shrink-0 border border-slate-100" />
                    : <div className="w-[58px] h-[58px] rounded-xl bg-slate-100 flex-shrink-0 flex items-center justify-center text-[12px] font-bold text-slate-400">{item.name?.[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <Link to={getLink(`/product/${item.slug || item.product_id}`)} onClick={close}
                      className="text-[13px] font-medium text-slate-800 line-clamp-2 hover:underline" data-testid={`cart-item-link-${item.product_id}`}>{item.name}</Link>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.variation)} className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50"><Minus size={11} weight="bold" /></button>
                        <span className="w-7 h-7 flex items-center justify-center text-[12px] font-semibold text-slate-800">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.variation)} className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50"><Plus size={11} weight="bold" /></button>
                      </div>
                      <span className="text-[14px] font-semibold" style={{ color: primary }}>{(item.price * item.quantity).toFixed(2)} €</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.product_id, item.variation)} className="p-1.5 text-slate-300 hover:text-rose-500 flex-shrink-0" aria-label="Retirer"><Trash size={16} weight="bold" /></button>
                </div>
              ))}

              {suggested.length > 0 && (
                <div className="pt-4 pb-2" data-testid="suggested-products">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">On vous suggère aussi</p>
                  <div className="space-y-2">
                    {suggested.map(p => (
                      <div key={p.id} className="flex items-center gap-3">
                        <Link to={getLink(`/product/${p.slug}`)} onClick={close} className="flex items-center gap-3 flex-1 min-w-0">
                          {(p.featured_image || p.images?.[0])
                            ? <img src={p.featured_image || p.images[0]} alt={p.name} className="w-10 h-10 rounded-lg object-contain bg-slate-50 p-0.5 flex-shrink-0 border border-slate-100" loading="lazy" />
                            : <div className="w-10 h-10 rounded-lg bg-slate-100 flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-slate-400">{p.name?.[0]}</div>}
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] text-slate-600 truncate">{p.name}</p>
                            <span className="text-[12px] font-semibold" style={{ color: primary }}>{parseFloat(p.sale_price || p.price).toFixed(2)} €</span>
                          </div>
                        </Link>
                        <button onClick={() => addItem(p, 1, null, storeSlug)} className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0 hover:scale-105 transition-transform" style={{ backgroundColor: accent }} data-testid={`suggest-add-${p.id}`}><Plus size={13} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {count > 0 && (
          <div className="px-5 py-4 border-t border-slate-100 shrink-0 space-y-3">
            {!user && (
              <Link to={getLink('/account')} onClick={close} className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-testid="cart-login-prompt">
                <SignIn size={16} style={{ color: accent }} />
                <span className="flex-1 text-[12px] text-slate-600"><strong className="text-slate-800">Connectez-vous</strong> pour un suivi de commande</span>
                <ArrowRight size={13} className="text-slate-400" />
              </Link>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-slate-500">Total</span>
              <span className="text-[22px] font-semibold" style={{ color: primary }}>{(cartTotal || 0).toFixed(2)} €</span>
            </div>
            {taxBreakdown?.groups?.length > 0 && (
              <div className="space-y-0.5" data-testid="floating-cart-vat">
                {taxBreakdown.groups.map(g => (
                  <div key={g.rate} className="flex justify-between text-[11px] text-slate-400">
                    <span>Dont TVA {String(g.rate).replace('.', ',')}%</span>
                    <span>{g.vat.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            )}
            <Link to={getLink('/checkout')} onClick={close}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-white text-[15px] font-semibold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: accent }} data-testid="floating-cart-checkout">
              Commander <ArrowRight size={17} weight="bold" />
            </Link>
          </div>
        )}
      </div>

      <style>{`
        @keyframes cartFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cartSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
};

export default FloatingCart;
